import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Row, Col, Input, Select, Button, Space, Tag, Typography, Modal, Descriptions, message, Badge, Spin, Form, InputNumber, DatePicker } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import parasutService, { PaymentPayload } from '../../../../services/parasut/parasut.service';
import { EyeOutlined, ReloadOutlined, EditOutlined, PlusOutlined, FileProtectOutlined, DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { handleApiError } from '../../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Text } = Typography;

const currency = (n: number, c: string = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: c, maximumFractionDigits: 2 }).format(n || 0);

const ParasutInvoices: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  
  // Modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [officializeLoading, setOfficializeLoading] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null);
  
  // Forms
  const [paymentForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchList = async () => {
    try {
      setLoading(true);
      // Lokal veritabanındaki fatura kayıtlarını getir
      const params: any = {
        page: 1,
        limit: 25,
      };
      
      // Arama parametresi varsa ekle
      if (q && q.trim()) {
        params.search = q.trim();
      }
      
      // Durum filtresi varsa ekle
      if (status) {
        params.status = status;
      }
      
      const res = await parasutService.getInvoiceRecords(params);
      setItems(res);
    } catch (e: any) {
      message.error('Fatura kayıtları alınamadı');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [q, status]);

  // Düzenleme modalını aç
  const openEditModal = (record: any) => {
    setSelected(record);
    editForm.setFieldsValue({
      description: record.parasutInvoice?.attributes?.description || '',
      amount: record.parasutInvoice?.attributes?.net_total || 0,
    });
    setEditModalOpen(true);
  };

  // Fatura düzenleme
  const handleEditInvoice = async (values: any) => {
    if (!selected || !selected.record?.parasutInvoiceId) {
      message.warning('Düzenlemek için Parasut fatura ID\'si gereklidir');
      return;
    }
    
    try {
      // Parasut'ta faturayı güncelle
      await parasutService.updateSalesInvoice(selected.record.parasutInvoiceId, {
        description: values.description,
        amount: values.amount,
      });
      
      message.success('Fatura başarıyla güncellendi');
      setEditModalOpen(false);
      editForm.resetFields();
      fetchList();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  // Tahsilat modalını aç
  const openPaymentModal = (record: any) => {
    setSelected(record);
    paymentForm.setFieldsValue({
      amount: record.parasutInvoice?.attributes?.remaining || record.parasutInvoice?.attributes?.net_total || 0,
      date: dayjs(),
      description: `Şirket ID ${record.record?.companyId} - Tahsilat`,
      paymentMethod: 'bank_transfer',
    });
    setPaymentModalOpen(true);
  };

  // Tahsilat ekleme
  const handleAddPayment = async (values: any) => {
    if (!selected || !selected.record?.parasutInvoiceId) {
      message.warning('Tahsilat eklemek için Parasut fatura ID\'si gereklidir');
      return;
    }
    
    try {
      const payload: PaymentPayload = {
        amount: values.amount,
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        paymentMethod: values.paymentMethod,
      };
      
      await parasutService.addPayment(selected.record.parasutInvoiceId, payload);
      
      message.success('Tahsilat başarıyla eklendi');
      setPaymentModalOpen(false);
      paymentForm.resetFields();
      fetchList();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  // Fatura resmileştirme
  const handleOfficialize = async (record: any) => {
    if (!record.record?.parasutInvoiceId) {
      message.warning('Bu kayıt için Parasut fatura ID\'si bulunamadı');
      return;
    }

    Modal.confirm({
      title: 'Faturayı resmileştirmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: 'Bu işlem geri alınamaz ve fatura e-Fatura/e-Arşiv olarak resmileştirilecek.',
      okText: 'Evet, Resmileştir',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          setOfficializeLoading(record.record.id);
          const result = await parasutService.officializeInvoice(record.record.parasutInvoiceId);
          
          if (result.operationId) {
            message.loading({
              content: 'Resmileştirme işlemi başlatıldı, durum takip ediliyor...',
              key: `officialize_${record.record.id}`,
              duration: 0,
            });
            
            // İşlem durumunu takip et
            const finalStatus = await parasutService.pollOperationStatus(result.operationId);
            
            if (finalStatus.status === 'completed') {
              // Lokal kaydı güncelle
              await parasutService.updateInvoiceRecord(record.record.id, {
                status: 'official'
              });
              
              message.success({
                content: 'Fatura başarıyla resmileştirildi',
                key: `officialize_${record.record.id}`,
              });
              fetchList();
            } else {
              message.error({
                content: `Resmileştirme başarısız: ${finalStatus.error || 'Bilinmeyen hata'}`,
                key: `officialize_${record.record.id}`,
              });
            }
          } else {
            message.success('Fatura resmileştirme işlemi başlatıldı');
            fetchList();
          }
        } catch (error: any) {
          message.error({
            content: 'Resmileştirme hatası',
            key: `officialize_${record.record.id}`,
          });
          handleApiError(error, t);
        } finally {
          setOfficializeLoading(null);
        }
      },
    });
  };

  // PDF indirme
  const handleDownloadPdf = async (record: any) => {
    try {
      setDownloadLoading(record.record?.id);
      
      if (!record.record?.parasutInvoiceId) {
        message.warning('Bu kayıt için Parasut fatura ID\'si bulunamadı');
        return;
      }
      
      // Önce fatura detayını al
      const invoiceDetail = await parasutService.getSalesInvoice(record.record.parasutInvoiceId);
      // E-Fatura veya E-Arşiv ID'sini kontrol et
      if (invoiceDetail.data.relationships.active_e_document.data.id) {
        const filename = `e-fatura-${record.record.parasutInvoiceId}.pdf`;
        
        if(invoiceDetail.data.relationships.active_e_document.data.type === 'e_invoices') {
          
          await parasutService.downloadPdf(`/parasut/e-invoices/${invoiceDetail.data.relationships.active_e_document.data.id}/pdf`, filename);
          message.success('E-Fatura PDF\'i indirildi');
        } else if(invoiceDetail.data.relationships.active_e_document.data.type === 'e_archives') {
          
          await parasutService.downloadPdf(`/parasut/e-archives/${invoiceDetail.data.relationships.active_e_document.data.id}/pdf`, filename);
          message.success('E-Arşiv PDF\'i indirildi');
        }
      } else if (invoiceDetail.data.relationships.active_e_document.data.id) {
        const filename = `e-arsiv-${record.record.parasutInvoiceId}.pdf`;
        await parasutService.downloadPdf(`/parasut/e-archives/${invoiceDetail.data.relationships.active_e_document.data.id}/pdf`, filename);
        message.success('E-Arşiv PDF\'i indirildi');
      } else {
        message.warning('Bu fatura henüz resmileştirilmemiş, PDF mevcut değil');
      }
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setDownloadLoading(null);
    }
  };

  // Lokal kayıt için Parasut faturası oluşturma (Bu durumda zaten fatura var, bu fonksiyon kullanılmayacak)
  const handleCreateParasutInvoice = async (record: any) => {
    try {
      setOfficializeLoading(record.record?.id);
      
      // Bu durumda zaten parasutInvoice mevcut, sadece warning ver
      message.warning('Bu kayıt için zaten Parasut faturası mevcut');
      
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setOfficializeLoading(null);
    }
  };

  const columns: ColumnsType<any> = useMemo(() => ([
    {
      title: 'Kayıt ID',
      dataIndex: ['record', 'id'],
      key: 'id',
      width: 100,
      render: (v: number) => <Text code>#{v}</Text>
    },
    {
      title: 'Parasut Fatura ID',
      dataIndex: ['record', 'parasutInvoiceId'],
      key: 'parasutInvoiceId',
      width: 160,
      render: (v: string) => v ? <Text code>{String(v).slice(-8)}</Text> : <Text type="secondary">-</Text>
    },
    {
      title: 'Şirket',
      key: 'company',
      width: 200,
      ellipsis: true,
      render: (_: any, item: any) => {
        // Parasut fatura bilgilerinden şirket bilgisini al
        const parasutInvoice = item.parasutInvoice;
        const companyInfo = parasutInvoice?.relationships?.contact?.data;
        return (
          <div>
            <div className="fw-bold">Şirket ID: {item.record?.companyId}</div>
            {parasutInvoice?.attributes?.billing_address && (
              <div className="text-muted small">{parasutInvoice.attributes.billing_address}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Açıklama',
      key: 'desc',
      ellipsis: true,
      render: (_: any, item: any) => item.parasutInvoice?.attributes?.description || '-'
    },
    {
      title: 'Tutar',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_: any, item: any) => {
        const amount = item.parasutInvoice?.attributes?.net_total || 0;
        const currency_code = item.parasutInvoice?.attributes?.currency || 'TRY';
        return currency(Number(amount), currency_code === 'TRL' ? 'TRY' : currency_code);
      }
    },
    {
      title: 'Ödeme Durumu',
      key: 'paymentStatus',
      width: 140,
      render: (_: any, item: any) => {
        const paymentStatus = item.parasutInvoice?.attributes?.payment_status;
        const statusMap: { [key: string]: { status: any, text: string } } = {
          'paid': { status: 'success', text: 'Ödendi' },
          'unpaid': { status: 'warning', text: 'Ödenmedi' },
          'partially_paid': { status: 'processing', text: 'Kısmi Ödendi' },
          'overdue': { status: 'error', text: 'Gecikmiş' }
        };
        const statusInfo = statusMap[paymentStatus] || { status: 'default', text: paymentStatus || '-' };
        return <Badge status={statusInfo.status} text={statusInfo.text} />;
      }
    },
    {
      title: 'Durum',
      dataIndex: ['record', 'status'],
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusMap: { [key: string]: { status: any, text: string } } = {
          'unofficial': { status: 'default', text: 'Resmi Değil' },
          'official': { status: 'success', text: 'Resmi' },
          'cancelled': { status: 'error', text: 'İptal' }
        };
        const statusInfo = statusMap[status] || { status: 'default', text: status || '-' };
        return <Badge status={statusInfo.status} text={statusInfo.text} />;
      }
    },
    {
      title: 'Fatura Tarihi',
      key: 'issueDate',
      width: 120,
      render: (_: any, item: any) => {
        const date = item.parasutInvoice?.attributes?.issue_date;
        return date ? dayjs(date).format('DD.MM.YYYY') : '-';
      }
    },
    {
      title: 'Oluşturulma',
      dataIndex: ['record', 'createdAt'],
      key: 'createdAt',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 220,
      render: (_: any, r: any) => (
        <Space>
          <Button 
            size="small" 
            icon={<EyeOutlined />} 
            onClick={() => { setSelected(r); setModalOpen(true); }}
            title="Detayları Görüntüle"
          />
          {r.record?.parasutInvoiceId ? (
            <>
              {/* <Button 
                size="small" 
                icon={<EditOutlined />} 
                onClick={() => openEditModal(r)}
                title="Faturayı Düzenle"
                disabled={r.record?.status === 'official' || r.record?.status === 'cancelled'}
              /> */}
              {/* <Button 
                size="small" 
                icon={<PlusOutlined />} 
                onClick={() => openPaymentModal(r)}
                title="Tahsilat Ekle"
                disabled={r.parasutInvoice?.attributes?.payment_status === 'paid' || r.record?.status === 'cancelled'}
              /> */}
              <Button 
                size="small" 
                icon={<FileProtectOutlined />} 
                onClick={() => handleOfficialize(r)}
                loading={officializeLoading === r.record?.id}
                title="Resmileştir"
                disabled={r.record?.status === 'official' || r.record?.status === 'cancelled'}
              />
              <Button 
                size="small" 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownloadPdf(r)}
                loading={downloadLoading === r.record?.id}
                title="PDF İndir"
              />
            </>
          ) : (
            <Button 
              size="small" 
              type="primary"
              icon={<PlusOutlined />} 
              onClick={() => handleCreateParasutInvoice(r)}
              loading={officializeLoading === r.record?.id}
              title="Parasut Faturası Oluştur"
            >
              Fatura Oluştur
            </Button>
          )}
        </Space>
      )
    }
  ]), [officializeLoading, downloadLoading]);

  return (
    <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
      <div className="content flex-fill h-100">
        <div className="p-6">
          <Row gutter={[12, 12]} align="middle" className="mb-4">
            <Col xs={24} md={8}>
              <Input placeholder="Şirket / açıklama ara" value={q} onChange={(e) => setQ(e.target.value)} allowClear />
            </Col>
            <Col xs={24} md={6}>
              <Select placeholder="Durum" allowClear style={{ width: '100%' }} value={status} onChange={(v) => setStatus(v)}>
                <Option value="unofficial">Resmi Değil</Option>
                <Option value="official">Resmi</Option>
                <Option value="cancelled">İptal</Option>
              </Select>
            </Col>
            <Col xs={24} md={10}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchList}>Yenile</Button>
              </Space>
            </Col>
          </Row>

          <Card>
            <Table
              columns={columns}
              dataSource={items}
              rowKey={(r) => r.record?.id || r.id}
              loading={loading}
              pagination={{ pageSize: 25 }}
              scroll={{ x: 1400 }}
            />
          </Card>
        </div>
      </div>

      {/* Fatura Detay Modal */}
      <Modal open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} title="Fatura Kaydı Detayı" width={900}>
        {!selected ? <Spin /> : (
          <Descriptions bordered size="small" column={2} labelStyle={{ width: 180 }}>
            <Descriptions.Item label="Kayıt ID">#{selected.record?.id}</Descriptions.Item>
            <Descriptions.Item label="Parasut Fatura ID">{selected.record?.parasutInvoiceId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Şirket ID">{selected.record?.companyId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Parasut Contact ID">{selected.record?.parasutContactId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Açıklama" span={2}>{selected.parasutInvoice?.attributes?.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="Net Tutar">{currency(Number(selected.parasutInvoice?.attributes?.net_total || 0), selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY')}</Descriptions.Item>
            <Descriptions.Item label="Brüt Tutar">{currency(Number(selected.parasutInvoice?.attributes?.gross_total || 0), selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY')}</Descriptions.Item>
            <Descriptions.Item label="KDV Tutarı">{currency(Number(selected.parasutInvoice?.attributes?.total_vat || 0), selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY')}</Descriptions.Item>
            <Descriptions.Item label="Ödenen Tutar">{currency(Number(selected.parasutInvoice?.attributes?.total_paid || 0), selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY')}</Descriptions.Item>
            <Descriptions.Item label="Kalan Tutar">{currency(Number(selected.parasutInvoice?.attributes?.remaining || 0), selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY')}</Descriptions.Item>
            <Descriptions.Item label="Para Birimi">{selected.parasutInvoice?.attributes?.currency === 'TRL' ? 'TRY' : selected.parasutInvoice?.attributes?.currency || 'TRY'}</Descriptions.Item>
            <Descriptions.Item label="Ödeme Durumu">
              {(() => {
                const paymentStatus = selected.parasutInvoice?.attributes?.payment_status;
                const statusMap: { [key: string]: { status: any, text: string } } = {
                  'paid': { status: 'success', text: 'Ödendi' },
                  'unpaid': { status: 'warning', text: 'Ödenmedi' },
                  'partially_paid': { status: 'processing', text: 'Kısmi Ödendi' },
                  'overdue': { status: 'error', text: 'Gecikmiş' }
                };
                const statusInfo = statusMap[paymentStatus] || { status: 'default', text: paymentStatus || '-' };
                return <Badge status={statusInfo.status} text={statusInfo.text} />;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Kayıt Durumu">
              {(() => {
                const recordStatus = selected.record?.status;
                const statusMap: { [key: string]: { status: any, text: string } } = {
                  'unofficial': { status: 'default', text: 'Resmi Değil' },
                  'official': { status: 'success', text: 'Resmi' },
                  'cancelled': { status: 'error', text: 'İptal' }
                };
                const statusInfo = statusMap[recordStatus] || { status: 'default', text: recordStatus || '-' };
                return <Badge status={statusInfo.status} text={statusInfo.text} />;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Fatura Tarihi">{selected.parasutInvoice?.attributes?.issue_date ? dayjs(selected.parasutInvoice.attributes.issue_date).format('DD.MM.YYYY') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Vade Tarihi">{selected.parasutInvoice?.attributes?.due_date ? dayjs(selected.parasutInvoice.attributes.due_date).format('DD.MM.YYYY') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Faturalama Adresi" span={2}>{selected.parasutInvoice?.attributes?.billing_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="Telefon">{selected.parasutInvoice?.attributes?.billing_phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Vergi Numarası">{selected.parasutInvoice?.attributes?.tax_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Vergi Dairesi">{selected.parasutInvoice?.attributes?.tax_office || '-'}</Descriptions.Item>
            <Descriptions.Item label="Şehir/İlçe">{selected.parasutInvoice?.attributes?.city && selected.parasutInvoice?.attributes?.district ? `${selected.parasutInvoice.attributes.city} / ${selected.parasutInvoice.attributes.district}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="Oluşturulma">{selected.record?.createdAt ? dayjs(selected.record.createdAt).format('DD.MM.YYYY HH:mm') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Parasut Oluşturma">{selected.parasutInvoice?.attributes?.created_at ? dayjs(selected.parasutInvoice.attributes.created_at).format('DD.MM.YYYY HH:mm') : '-'}</Descriptions.Item>
            <Descriptions.Item label="E-Belge Türü">{selected.record?.eDocumentKind || '-'}</Descriptions.Item>
            <Descriptions.Item label="Hata Mesajı">{selected.record?.errorMessage || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Fatura Düzenleme Modal */}
      <Modal
        title="Fatura Düzenle"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditInvoice}
        >
          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama zorunludur' }]}
          >
            <Input.TextArea rows={3} placeholder="Fatura açıklaması" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Tutar"
            rules={[{ required: true, message: 'Tutar zorunludur' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="0.00"
            />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalOpen(false);
                editForm.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                Güncelle
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Tahsilat Ekleme Modal */}
      <Modal
        title="Tahsilat Ekle"
        open={paymentModalOpen}
        onCancel={() => {
          setPaymentModalOpen(false);
          paymentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handleAddPayment}
        >
          <Form.Item
            name="amount"
            label="Tahsilat Tutarı"
            rules={[{ required: true, message: 'Tutar zorunludur' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="Tahsilat Tarihi"
            rules={[{ required: true, message: 'Tarih zorunludur' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input placeholder="Tahsilat açıklaması" />
          </Form.Item>

          <Form.Item
            name="paymentMethod"
            label="Ödeme Yöntemi"
            rules={[{ required: true, message: 'Ödeme yöntemi zorunludur' }]}
          >
            <Select placeholder="Ödeme yöntemi seçin">
              <Option value="bank_transfer">Banka Havalesi</Option>
              <Option value="credit_card">Kredi Kartı</Option>
              <Option value="cash">Nakit</Option>
              <Option value="check">Çek</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setPaymentModalOpen(false);
                paymentForm.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                Tahsilat Ekle
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ParasutInvoices;


